import { buildAnnotatedImageAssignments } from '../utils/annotatedImages';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Send, Loader2, AlertCircle, Bot, ShoppingCart, X, Minus, Plus, CheckCircle, Package, Trash2, Paperclip, Mic, MicOff, FileText, RotateCcw, Flag, Square, WifiOff, Sparkles, ChevronDown, PencilLine } from 'lucide-react';
import { Message, BotMessage, UserMessage, StructuredAnswer, PartsTrackerPanel } from '../components/features/chat';
import CatalogStatusStrip from '../components/features/chat/CatalogStatusStrip';
import { useTrackedParts } from '../hooks/useTrackedParts';
import RecordingWaveform from '../components/features/chat/RecordingWaveform';
import { OrderProvider } from '../contexts/OrderContext';
import { finalizeMessage, reduceFrame } from '../utils/streamReducer';
import { parseStreamEvent } from '../utils/streamEffects';
import { parseCatalogQuantity } from '../utils/quantity';
import { ContinuationFrame, FinalFrame } from '../types/streamProtocol';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { transcribeAudio } from '../services/TranscriptionService';
import chatService from '../services/ChatService';
import { displayPartRef } from '../utils/partLabel';
import { isPersistedPartEditInstruction, parsePersistedPartEdits } from '../utils/partEdits';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface GuestSession {
  access_token: string;
  expires_in: number;
  organization_name: string;
  language: string;
  guest_uid: string;
  organization_id: number;
  // Visitor metadata resolved by backend (invite pre-fill + URL override)
  visitor_name: string | null;
  visitor_email: string | null;
  visitor_phone: string | null;
  // Branding set by the org admin on this invite link
  icon_url: string | null;
  header_text: string | null;
  // Domain restriction — null means no restriction
  allowed_domain: string | null;
  // Vehicle / extra context passed via catax_ctx
  vin: string | null;
  license_plate: string | null;
  model: string | null;
  manufacturer: string | null;
  year: number | null;
  extra: unknown | null;
}

// ─────────────────────────────────────────────
// Helper: base URL
// ─────────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL || '';

// ─────────────────────────────────────────────
// Module-level in-flight deduplication map for POST /api/guest/session.
//
// React StrictMode (dev) mounts every component twice, causing the
// useEffect([token]) to fire twice in rapid succession.  Without this guard
// both invocations race to call POST /api/guest/session simultaneously, which
// generates TWO different guest_uid values.  The session is created under uid-A
// but subsequent chat requests arrive with the JWT from uid-B → "session not found".
//
// Solution: the first call stores its Promise here keyed by token.  The second
// call (same token, same tick) awaits the same Promise and receives the same
// GuestSession object — only one HTTP request is ever made.  The entry is
// cleared when the Promise settles so a genuine retry (e.g. after a network
// error) starts fresh.
// ─────────────────────────────────────────────
const _guestSessionInflight = new Map<string, Promise<GuestSession>>();

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const GuestChatPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // ── Decode catax_ctx (single encoded JSON param sent by the SDK) ───────────
  // Falls back to individual ?vn= / ?ve= / ?vp= / ?lang= params for direct links.
  const cataxCtxRaw = searchParams.get('catax_ctx');
  const cataxCtx: Record<string, unknown> = (() => {
    if (!cataxCtxRaw) return {};
    try { return JSON.parse(decodeURIComponent(cataxCtxRaw)); } catch { return {}; }
  })();

  const qs = (short: string, long: string) => searchParams.get(short) || searchParams.get(long) || undefined;
  const urlVisitorName  = (cataxCtx.visitor_name  as string | undefined) ?? qs('vn',   'visitor_name');
  const urlVisitorEmail = (cataxCtx.visitor_email as string | undefined) ?? qs('ve',   'visitor_email');
  const urlVisitorPhone = (cataxCtx.visitor_phone as string | undefined) ?? qs('vp',   'visitor_phone');
  const urlLanguage     = (cataxCtx.language      as string | undefined) ?? qs('lang', 'language');

  // ExtraInfo — only available via catax_ctx
  const urlVin          = cataxCtx.vin           as string | undefined;
  const urlLicensePlate = cataxCtx.license_plate as string | undefined;
  const urlModel        = cataxCtx.model         as string | undefined;
  const urlManufacturer = cataxCtx.manufacturer  as string | undefined;
  const urlYear         = cataxCtx.year          as number | undefined;
  const urlExtra        = cataxCtx.extra;

  // If the user refreshes, the session_id is preserved in the URL so we can resume the conversation.
  const urlSessionId    = searchParams.get('sid') || undefined;
  // The guest_uid is also stored in the URL so that if sessionStorage is unavailable
  // (incognito, cross-origin iframe, new tab), the backend can reuse the same identity
  // instead of generating a fresh uid that won't match the existing ADK session.
  const urlGuestUid     = searchParams.get('uid') || undefined;

  // Guest session state
  const [guestSession, setGuestSession] = useState<GuestSession | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [domainBlocked, setDomainBlocked] = useState(false);

  // Report error modal state
  const [reportErrorOpen, setReportErrorOpen] = useState(false);
  const [reportErrorComment, setReportErrorComment] = useState('');
  const [reportErrorContext, setReportErrorContext] = useState<string>('');
  const [reportErrorMessage, setReportErrorMessage] = useState<string>('');
  const [reportErrorSubmitting, setReportErrorSubmitting] = useState(false);
  const [reportErrorDone, setReportErrorDone] = useState(false);

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  // One assignment pass over the whole thread so a marked photo renders
  // exactly once (it rides along on several messages of the same turn).
  const annotatedImageAssignments = React.useMemo(
    () => buildAnnotatedImageAssignments(messages),
    [messages],
  );
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);   // stays in sync for callbacks
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Stable refs so the visibilitychange handler never captures stale state
  const sessionIdRef = useRef<string | null>(null);
  const guestSessionRef = useRef<GuestSession | null>(null);

  // Guard against React StrictMode double-invocation: ensures only ONE
  // create_session call is in flight at a time.
  const sessionCreatingRef = useRef(false);

  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([]);
  // Requested-parts tracker — the SAME derivation the signed-in chat uses. Without
  // it BotMessage falls back to its legacy in-bubble parts list, which is the old
  // list guest sessions were still showing.
  const {
    trackedParts,
    trackedServiceTitle,
    handleValidationParts,
    trackerFoundParts,
    trackerAnnotatedImages,
    trackerVisionPartImages,
    trackerNotFoundParts,
  } = useTrackedParts(messages, sessionId);
  const [showCart, setShowCart] = useState(false);
  const [cartSubmitted, setCartSubmitted] = useState(false);
  const [isSubmittingCart, setIsSubmittingCart] = useState(false);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<Array<{ data: string; mimeType: string; preview: string; name: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Toast notifications (replaces native alert)
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'error' | 'info' }>>([]);
  const showToast = useCallback((message: string, type: 'error' | 'info' = 'error') => {
    const id = `toast_${Date.now()}`;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4500);
  }, []);

  // Offline banner
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const handleOnline  = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Scroll-to-bottom button visibility
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const mainScrollRef = useRef<HTMLElement | null>(null);

  // Audio recording
  const [audioError, setAudioError] = useState<string | null>(null);
  const { status: audioStatus, isSupported: audioSupported, toggle: toggleAudio, stop: stopAudio } = useAudioRecorder({
    onAudioFile: (base64, mimeType) => {
      const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
      const audioFile = {
        data: base64,
        mimeType,
        preview: '',
        name: `voice-message.${ext}`,
        isTranscribing: true,
      };
      setSelectedFiles(prev => [...prev, audioFile]);
      // Trigger send on next tick so state has updated
      setTimeout(() => sendMessage(), 0);
      // Transcribe in the background — the sent message will be patched via setMessages
      transcribeAudio(base64, mimeType, sessionId ?? undefined, guestSession?.language ?? undefined)
        .then((transcript) => {
          setSelectedFiles(prev => prev.map(f =>
            f.name === audioFile.name && f.mimeType === mimeType
              ? { ...f, transcript: transcript ?? undefined, isTranscribing: false }
              : f
          ));
        })
        .catch(() => {
          setSelectedFiles(prev => prev.map(f =>
            f.name === audioFile.name && f.mimeType === mimeType
              ? { ...f, isTranscribing: false }
              : f
          ));
        });
    },
    onError: (msg) => {
      setAudioError(msg);
      setTimeout(() => setAudioError(null), 4000);
    },
  });
  const isRecording = audioStatus === 'recording';
  const isProcessingAudio = audioStatus === 'processing';

  // ── Persist cart to sessionStorage whenever it changes ───────────────────
  const cartStorageKey = sessionId ? `guest_cart_${sessionId}` : null;

  // Restore cart from sessionStorage when sessionId is first set
  useEffect(() => {
    if (!sessionId) return;
    const key = `guest_cart_${sessionId}`;
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setCartItems(parsed);
        }
      }
    } catch { /* non-fatal */ }
  }, [sessionId]);

  // Persist cart to sessionStorage on every change
  useEffect(() => {
    if (!cartStorageKey) return;
    try {
      sessionStorage.setItem(cartStorageKey, JSON.stringify(cartItems));
    } catch { /* non-fatal */ }
  }, [cartItems, cartStorageKey]);

  // keep refs in sync with state
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { guestSessionRef.current = guestSession; }, [guestSession]);

  // Route shared components' section-diagram fetches through the guest endpoint
  // (apiService has no auth for guests — without this the clarification diagram
  // always renders "Diagram not available").
  useEffect(() => {
    chatService.setGuestAuth(BASE_URL, guestSession?.access_token ?? null);
    return () => chatService.setGuestAuth(BASE_URL, null);
  }, [guestSession]);

  // No SessionProvider on this page — register the active session id so shared
  // components (diagram fetch) know which session they belong to.
  useEffect(() => {
    chatService.setGuestSessionId(sessionId);
    return () => chatService.setGuestSessionId(null);
  }, [sessionId]);

  // ── helpers ───────────────────────────────
  const authHeaders = useCallback(
    (extra: Record<string, string> = {}) => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${guestSession?.access_token ?? ''}`,
      ...extra,
    }),
    [guestSession],
  );

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') =>
    bottomRef.current?.scrollIntoView({ behavior });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Show/hide scroll-to-bottom button based on scroll position
  useEffect(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(distFromBottom > 200);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
    // We want this effect to (re-)run whenever mainScrollRef.current is populated.
    // Using messages as a proxy: the ref is guaranteed set after the first render,
    // and messages changing triggers a re-check which is fine.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length > 0]);

  // ── Switch UI language: ?lang= / ?language= query param wins; otherwise
  // follow the guest session's org language (from the token exchange), so the
  // page chrome ("Select one", diagram tabs…) matches the language the backend
  // answers in even when the invite link carries no lang param. Without this
  // fallback the chrome stayed on the browser default (often English) while
  // the messages arrived in the org language — mixed-language UI.
  useEffect(() => {
    const target = urlLanguage || guestSession?.language;
    if (target && target !== i18n.language) {
      i18n.changeLanguage(target);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlLanguage, guestSession?.language]);

  // ── Step 1: exchange invite token → guest JWT ─
  useEffect(() => {
    if (!token) {
      setInitError('Invalid invite link.');
      setInitializing(false);
      return;
    }

    /**
     * Client-side domain guard.
     * Returns true when the page is allowed to use this session.
     * When running inside an iframe the real host is window.parent's origin,
     * which we can read from document.referrer (browsers always set this for
     * cross-origin iframes). For direct visits we use window.location.hostname.
     */
    const checkDomainAllowed = (session: GuestSession): boolean => {
      if (!session.allowed_domain) return true; // no restriction configured
      const allowed = session.allowed_domain.split('://').pop()!.replace(/\/$/, '').toLowerCase();
      // Determine actual host: prefer referrer (set by iframe parent) over own location
      const referrerHost = document.referrer
        ? new URL(document.referrer).hostname.toLowerCase()
        : null;
      const locationHost = window.location.hostname.toLowerCase();
      const host = referrerHost || locationHost;
      // Allow exact match or any subdomain
      return host === allowed || host.endsWith(`.${allowed}`);
    };

    // Reuse cached guest JWT within the same browser tab (sessionStorage is tab-scoped).
    // We intentionally do NOT cache or reuse the chat session_id — every page load
    // (including a new tab or refresh) gets a brand-new session so conversations are isolated.
    const cached = sessionStorage.getItem(`guest_session_${token}`);
    if (cached) {
      try {
        const parsed: GuestSession = JSON.parse(cached);
        // Override cached visitor metadata with any fresh URL query params so that
        // a new visitor opening the same invite link with ?name=...&email=... always
        // gets the correct identity forwarded to the chat session state.
        const merged: GuestSession = {
          ...parsed,
          visitor_name:  urlVisitorName  ?? parsed.visitor_name,
          visitor_email: urlVisitorEmail ?? parsed.visitor_email,
          visitor_phone: urlVisitorPhone ?? parsed.visitor_phone,
          language:      urlLanguage     ?? parsed.language,
          // extraInfo fields always come from the URL (not cached — they may change per embed)
          vin:           urlVin          ?? parsed.vin,
          license_plate: urlLicensePlate ?? parsed.license_plate,
          model:         urlModel        ?? parsed.model,
          manufacturer:  urlManufacturer ?? parsed.manufacturer,
          year:          urlYear         ?? parsed.year,
          extra:         urlExtra        ?? parsed.extra,
        };
        if (!checkDomainAllowed(merged)) {
          setDomainBlocked(true);
          setInitializing(false);
          return;
        }
        setGuestSession(merged);
        if (urlSessionId) {
          // Resume existing conversation — ?sid= was set by this tab's own replaceState,
          // so the session is guaranteed to exist on the backend.
          (async () => {
            sessionIdRef.current = urlSessionId;
            setSessionId(urlSessionId);
            await loadSessionHistory(urlSessionId, merged.access_token);
            setInitializing(false);
          })();
        } else {
          // No ?sid= in URL → always create a fresh chat session.
          // Also clear any stale ?sid= / ?uid= that may linger from a previous visit.
          const next = new URLSearchParams(searchParams);
          next.delete('sid');
          next.delete('uid');
          window.history.replaceState(null, '', next.toString() ? `?${next.toString()}` : window.location.pathname);
          startChatSession(merged);
        }
        return;
      } catch {
        sessionStorage.removeItem(`guest_session_${token}`);
      }
    }

    (async () => {
      try {
        // ── Deduplicate concurrent POST /api/guest/session calls ──────────────
        // React StrictMode fires this effect twice simultaneously.  We store the
        // in-flight Promise in a module-level map so both invocations share the
        // same HTTP request and therefore receive the same guest_uid.
        // Detect the parent domain when the chat is embedded via the CataxSDK iframe.
        // Browsers always populate document.referrer for cross-origin iframes, so
        // this reliably gives us the embedding site's origin.  For direct visits
        // (no parent frame) we fall back to window.location.origin.
        // Prefer parent_origin injected by the CataxSDK (it runs on the embedding page
        // and reads window.location.origin directly — 100% reliable).
        // Fall back to document.referrer for direct iframe embeds without the SDK,
        // and finally to window.location.origin for plain direct visits.
        const parentDomain = (() => {
          if (cataxCtx.parent_origin) return cataxCtx.parent_origin as string;
          try {
            if (document.referrer) return new URL(document.referrer).origin;
          } catch { /* ignore malformed referrer */ }
          return window.location.origin;
        })();

        const inflightKey = `${token}:${urlGuestUid ?? ''}`;
        let sessionPromise = _guestSessionInflight.get(inflightKey);
        if (!sessionPromise) {
          sessionPromise = fetch(`${BASE_URL}/api/guest/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invite_token:       token,
              visitor_name:       urlVisitorName,
              visitor_email:      urlVisitorEmail,
              visitor_phone:      urlVisitorPhone,
              language:           urlLanguage,
              vin:                urlVin,
              license_plate:      urlLicensePlate,
              model:              urlModel,
              manufacturer:       urlManufacturer,
              year:               urlYear,
              extra:              urlExtra,
              // Pass back the uid from the URL so the backend reissues a JWT for the
              // same guest identity → the existing ADK session (keyed on user_id) is found.
              existing_guest_uid: urlGuestUid,
              // The domain of the page embedding this chat (via CataxSDK iframe or direct visit).
              // Used by the backend to enforce allowed_domain restrictions and for analytics.
              parent_domain:      parentDomain,
            }),
          }).then(async (res) => {
            if (!res.ok) {
              const err = await res.json().catch(() => ({}));
              throw new Error(err.detail ?? `Error ${res.status}`);
            }
            return res.json() as Promise<GuestSession>;
          }).finally(() => {
            // Clear the entry so a genuine retry starts fresh
            _guestSessionInflight.delete(inflightKey);
          });
          _guestSessionInflight.set(inflightKey, sessionPromise);
        }

        const data = await sessionPromise;
        // Client-side domain guard (backend already checked, this is a defence-in-depth layer)
        if (!checkDomainAllowed(data)) {
          setDomainBlocked(true);
          setInitializing(false);
          return;
        }
        sessionStorage.setItem(`guest_session_${token}`, JSON.stringify(data));
        setGuestSession(data);
        if (urlSessionId) {
          // A ?sid= is already in the URL (e.g. opened in a new tab after a refresh).
          // Resume that session instead of creating a brand-new one.
          sessionIdRef.current = urlSessionId;
          setSessionId(urlSessionId);
          await loadSessionHistory(urlSessionId, data.access_token);
          setInitializing(false);
        } else {
          await startChatSession(data);
        }
      } catch (e: any) {
        setInitError(e.message ?? 'Failed to start session.');
        setInitializing(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Load history for a resumed session ───────
  const loadSessionHistory = useCallback(async (sid: string, accessToken: string) => {
    try {
      const res = await fetch(`${BASE_URL}/api/guest/chat/session/${sid}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const history: Message[] = (data.session?.conversation_history ?? []).map((item: any) => {
        let messageText = item.text ?? '';
        if (item.sender === 'bot' && item.structured_data) {
          messageText = item.structured_data.text_message || messageText;
        }

        const structuredData = item.structured_data || null;

        let parts_results = undefined;
        if (item.parts_result && Array.isArray(item.parts_result) && item.parts_result.length > 0) {
          parts_results = item.parts_result;
        } else if (item.image || item.parts_list) {
          parts_results = [{
            image: item.image,
            image_callout: item.image_callout,
            part_callout_id: item.part_callout_id,
            complementary_parts: item.parts_list,
            structured_data: structuredData,
          }];
        }

        return {
          id: item.id ?? `h_${Math.random()}`,
          sender: item.sender as 'user' | 'bot',
          text: messageText,
          vin: item.vin || null,
          datasetId: null,
          timestamp: item.timestamp ? new Date(item.timestamp * 1000) : new Date(),
          isStreaming: false,
          events: undefined,
          parts_results,
          structured_data: structuredData,
          annotated_images: item.annotated_images || null,
          repair_instructions: item.repair_instructions || null,
          search_summarization: item.search_summarization || null,
          not_found_parts: item.not_found_parts || null,
          requested_parts: item.requested_parts || null,
          context_tags: item.context_tags || null,
          callToAction: item.callToAction || item.call_to_action || null,
          cta_actions: item.cta_actions || null,
        };
      });
      if (history.length > 0) setMessages(history);
    } catch {
      // History load failure is non-fatal — the user can still continue chatting.
    }
  }, []);

  // ── Step 2: create chat session ──────────────
  // Always creates a new session — never reuses a previous one.
  const startChatSession = async (session: GuestSession) => {
    // Guard: prevent duplicate calls (React StrictMode fires effects twice in dev,
    // and this function may also be called from multiple code paths).
    // We use a ref (not state) so the check is synchronous and race-free.
    if (sessionCreatingRef.current) return;
    sessionCreatingRef.current = true;
    try {
      const res = await fetch(`${BASE_URL}/api/guest/chat/create_session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          language:      session.language,
          // Forward visitor metadata (resolved by backend: URL override merged with invite pre-fill)
          visitor_name:  session.visitor_name  ?? undefined,
          visitor_email: session.visitor_email ?? undefined,
          visitor_phone: session.visitor_phone ?? undefined,
          // Forward extraInfo / vehicle context
          vin:           session.vin           ?? undefined,
          license_plate: session.license_plate ?? undefined,
          model:         session.model         ?? undefined,
          manufacturer:  session.manufacturer  ?? undefined,
          year:          session.year          ?? undefined,
          extra:         session.extra         ?? undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? `Error ${res.status}`);
      }

      const { session_id } = await res.json();
      // Sync refs immediately — before any React re-render touches them.
      // guestSessionRef is also synced here so handleAutoTriggerNext can read
      // the fresh session object even before the setGuestSession state flush.
      sessionIdRef.current = session_id;
      guestSessionRef.current = session;
      setSessionId(session_id);
      // Persist session_id AND guest_uid in the URL so a page refresh (or a new tab
      // opened from this URL) can pass the same guest_uid back to POST /api/guest/session
      // and get a JWT for the same identity — preventing "session not found" errors
      // caused by the backend generating a different uid on re-auth.
      const next = new URLSearchParams(searchParams);
      next.set('sid', session_id);
      next.set('uid', session.guest_uid);
      window.history.replaceState(null, '', `?${next.toString()}`);

      // Signal the useEffect([sessionId]) to fire the auto-trigger once React
      // flushes the setSessionId state update.  By that time sessionIdRef and
      // guestSessionRef are already populated with fresh values, so
      // handleAutoTriggerNext will use the correct session even on first incognito load.
      shouldAutoTriggerRef.current = true;
      sessionCreatingRef.current = false; // reset so future resets / reloads can create new sessions
    } catch (e: any) {
      sessionCreatingRef.current = false; // allow retry on error
      setInitError(e.message ?? 'Failed to create chat session.');
    } finally {
      setInitializing(false);
    }
  };

  // Stable ref so handleSubmitCart can call startChatSession without dep-array issues
  const startChatSessionRef = useRef(startChatSession);
  useEffect(() => { startChatSessionRef.current = startChatSession; });

  // Track whether the current session is brand-new (should auto-trigger NEXT)
  const shouldAutoTriggerRef = useRef(false);

  // ── Parse UPDATE_USER_DETAILS action from bot response text ─────────────
  const handleUpdateUserDetails = useCallback(async (
    payload: { visitor_name?: string | null; visitor_email?: string | null; visitor_phone?: string | null },
  ) => {
    if (!sessionId || !guestSession) return;
    // Update local guestSession state so subsequent calls use the new values
    setGuestSession(prev => {
      if (!prev) return prev;
      const updated: GuestSession = {
        ...prev,
        visitor_name:  payload.visitor_name  ?? prev.visitor_name,
        visitor_email: payload.visitor_email ?? prev.visitor_email,
        visitor_phone: payload.visitor_phone ?? prev.visitor_phone,
      };
      // Persist to sessionStorage so refreshes retain the data
      if (token) sessionStorage.setItem(`guest_session_${token}`, JSON.stringify(updated));
      return updated;
    });
    // Persist to backend session state
    try {
      await fetch(`${BASE_URL}/api/guest/chat/session/${sessionId}/visitor`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
    } catch { /* non-fatal */ }
  }, [sessionId, guestSession, authHeaders, token]);

  // ── Shared SSE streaming core ─────────────────
  // overrideSessionId / overrideGuestSession let callers bypass React state entirely
  // (critical for the first auto-trigger which fires before state has been flushed).
  const streamToMessage = useCallback(async (
    messageText: string,
    botMsgId: string,
    signal: AbortSignal,
    uploadedFiles?: Array<{ data: string; mimeType: string }>,
    overrideSessionId?: string,
    overrideGuestSession?: GuestSession,
    structuredAnswer?: Record<string, unknown>,
  ) => {
    const STALL_TIMEOUT_MS    =  60_000; // 60 s — backend heartbeats every 10 s during tool calls; plenty of margin
    const FETCH_TIMEOUT_MS    =  20_000; // 20 s to receive first response headers
    const MAX_RETRY_ATTEMPTS  = 4;
    const RETRY_BASE_DELAY    = 1_500;
    const RETRY_MAX_DELAY     = 12_000;

    // Pre-flight: fail fast when the device is definitely offline
    if (!navigator.onLine) {
      throw new Error('offline');
    }

    // Stable idempotency key — generated once and reused on every retry
    const clientMessageUuid: string =
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

    // Persists across retries so the server can skip already-delivered events
    let lastEventId: string | null = null;
    let retryCount = 0;

    // Tracks the active bot bubble ID — updated when a continuation opens a new bubble.
    // Declared here so the retry helper can reference it across attempts.
    let activeBotId = botMsgId;

    // Reset the active bubble before each retry so already-streamed events don't
    // accumulate and produce duplicate messages.  SSE resume (Last-Event-ID) tells
    // the server where to continue, so the bubble will be rebuilt cleanly.
    const resetActiveBubble = () => {
      setMessages(prev =>
        prev.map(m =>
          m.id !== activeBotId
            ? m
            : { ...m, text: '', events: [], isStreaming: true },
        ),
      );
    };

    // Outer resolve/reject for the Promise that wraps the whole attemptStream loop.
    // This lets the stall-timer callback (which runs outside the async call stack)
    // properly reject the outer promise instead of throwing into the void.
    let _outerResolve!: () => void;
    let _outerReject!: (err: any) => void;
    const outerPromise = new Promise<void>((res, rej) => { _outerResolve = res; _outerReject = rej; });

    // ── Online recovery — immediately retry when network comes back ───────
    // Without this, the stall timer (30 s) would be the only recovery path.
    let outerSettled = false;
    const handleOnline = () => {
      if (!outerSettled && retryCount < MAX_RETRY_ATTEMPTS) {
        retryCount++;
        resetActiveBubble();
        setTimeout(() => attemptStream().then(() => { outerSettled = true; _outerResolve(); }).catch(_outerReject), 500);
      }
    };
    window.addEventListener('online', handleOnline);
    // Clean up the online listener once the outer promise settles
    outerPromise.finally(() => { outerSettled = true; window.removeEventListener('online', handleOnline); });

    const attemptStream = async (): Promise<void> => {
      let stallTimer:   ReturnType<typeof setTimeout> | null = null;
      let readerRef: ReadableStreamDefaultReader<Uint8Array> | null = null;
      let errorFired = false;

      // ── Visibility-aware stall timer ─────────────────────────────────
      let stallPausedAt: number | null = null;
      let stallRemainingMs = 0;
      let lastDataReceived = Date.now();
      lastStreamDataRef.current = lastDataReceived; // sync shared ref on each attempt

      const scheduleStallTimeout = (delayMs: number) => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          if (signal.aborted) { readerRef?.cancel(); cleanup(); return; }
          readerRef?.cancel();
          if (!errorFired) {
            if (retryCount < MAX_RETRY_ATTEMPTS) {
              errorFired = true; // suppress the rejection from the cancelled reader.read()
              cleanup();
              readerRef = null;
              retryCount++;
              resetActiveBubble();
              const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
              // Schedule next attempt — result flows through outerPromise directly
              setTimeout(() => {
                errorFired = false; // allow the new attempt to report errors normally
                attemptStream()
                  .then(() => { outerSettled = true; _outerResolve(); })
                  .catch(_outerReject);
              }, delay);
            } else {
              errorFired = true;
              cleanup();
              // Reject the outer promise so the caller's catch block handles it
              _outerReject(new Error('server_error'));
            }
          }
        }, delayMs);
      };

      const pauseStallTimer = () => {
        if (stallTimer !== null && stallPausedAt === null) {
          clearTimeout(stallTimer);
          stallTimer = null;
          stallPausedAt = Date.now();
          stallRemainingMs = Math.max(0, STALL_TIMEOUT_MS - (stallPausedAt - lastDataReceived));
        }
      };

      const resumeStallTimer = () => {
        if (stallPausedAt !== null) {
          stallPausedAt = null;
          lastDataReceived = Date.now();
          scheduleStallTimeout(stallRemainingMs);
        }
      };

      const onActivity = () => {
        lastDataReceived = Date.now();
        lastStreamDataRef.current = lastDataReceived; // shared with visibility handler
        if (document.visibilityState !== 'hidden') {
          scheduleStallTimeout(STALL_TIMEOUT_MS);
        }
      };

      const handleVisibility = () =>
        document.visibilityState === 'hidden' ? pauseStallTimer() : resumeStallTimer();

      document.addEventListener('visibilitychange', handleVisibility);

      const cleanup = () => {
        if (stallTimer)   clearTimeout(stallTimer);
        if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
        document.removeEventListener('visibilitychange', handleVisibility);
      };

      // Build request — include idempotency key and SSE resume headers
      const resumeHeaders: Record<string, string> = {};
      if (lastEventId) {
        resumeHeaders['Last-Event-ID'] = lastEventId;
      }

      // Prefer explicit overrides over ref/state — overrides are set by startChatSession
      // at the exact moment the session_id is known, before any React re-render cycle.
      const effectiveSessionId   = overrideSessionId   ?? sessionIdRef.current;
      const effectiveGuestSession = overrideGuestSession ?? guestSession;

      // ── Fetch-level timeout — abort if server never responds with headers ──
      // This guards against the server accepting the TCP connection but never
      // sending HTTP headers (e.g. all workers busy, proxy queuing, cold start).
      const fetchAbortCtrl = new AbortController();
      let fetchTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        fetchAbortCtrl.abort(new Error('fetch_timeout'));
      }, FETCH_TIMEOUT_MS);

      // Combine the caller's abort signal with the fetch-level timeout signal
      const combinedSignal = (() => {
        // AbortSignal.any() is available in modern browsers; polyfill for older ones.
        if (typeof (AbortSignal as any).any === 'function') {
          return (AbortSignal as any).any([signal, fetchAbortCtrl.signal]) as AbortSignal;
        }
        // Fallback: forward the caller signal and rely on fetchAbortCtrl separately
        return signal;
      })();

      let response: Response;
      try {
        response = await fetch(`${BASE_URL}/api/guest/chat/endpoint`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${effectiveGuestSession?.access_token ?? ''}`,
            ...resumeHeaders,
          },
          body: JSON.stringify({
            message: messageText,
            session_id: effectiveSessionId,
            language: effectiveGuestSession?.language ?? 'en',
            client_message_uuid: clientMessageUuid,
            ...(lastEventId ? { last_event_id: lastEventId } : {}),
            ...(uploadedFiles && uploadedFiles.length > 0 ? { uploadedFiles } : {}),
            ...(structuredAnswer ? { structured_answer: structuredAnswer } : {}),
          }),
          signal: combinedSignal,
        }).finally(() => {
          // Headers received (or fetch failed) — cancel the fetch-level timeout
          if (fetchTimeoutId) { clearTimeout(fetchTimeoutId); fetchTimeoutId = null; }
        });
      } catch (fetchErr: any) {
        cleanup();
        // User-initiated abort — exit silently
        if (signal.aborted) return;
        // Fetch-level timeout or network error — retry if attempts remain
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          errorFired = true; // suppress rejection from this attempt
          retryCount++;
          resetActiveBubble();
          const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
          setTimeout(() => {
            errorFired = false;
            attemptStream()
              .then(() => { outerSettled = true; _outerResolve(); })
              .catch((err) => { if (!outerSettled) _outerReject(err); });
          }, delay);
          return;
        }
        throw new Error('server_error');
      }

      if (!response.ok) {
        cleanup();
        let detail = `Error ${response.status}`;
        try { const err = await response.json(); detail = err.detail ?? detail; } catch { /* ignore */ }
        if (response.status === 401) throw new Error('auth');
        if (response.status === 429) throw new Error('rate_limit');
        if (response.status === 499) return; // client-side abort — silently exit
        // Session not found (DB/multi-worker race on first message) — retry in-place.
        // The backend self-healing recreates the ADK session under the same ID,
        // so the same request will succeed after a brief delay.
        const isSessionMissing = (
          (response.status === 404 || response.status >= 500) &&
          detail.toLowerCase().includes('session') &&
          (detail.toLowerCase().includes('not found') || detail.toLowerCase().includes('expired'))
        );
        if (isSessionMissing) {
          if (retryCount < MAX_RETRY_ATTEMPTS) {
            errorFired = true;
            retryCount++;
            resetActiveBubble();
            const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
            setTimeout(() => {
              errorFired = false;
              attemptStream()
                .then(() => { outerSettled = true; _outerResolve(); })
                .catch((err) => { if (!outerSettled) _outerReject(err); });
            }, delay);
            return;
          }
          throw new Error('session_not_found');
        }
        if (response.status >= 500)  throw new Error('server_error');
        throw new Error(detail);
      }
      if (!response.body) { cleanup(); throw new Error('No response body'); }

      const reader = response.body.getReader();
      readerRef = reader;
      const decoder = new TextDecoder();
      let buffer = '';

      scheduleStallTimeout(STALL_TIMEOUT_MS);

      // Tracks the bot-bubble that is currently receiving events.
      // We use the outer `activeBotId` so continuation changes survive across
      // retry attempts and `resetActiveBubble` always targets the right bubble.
      const activeBotIdRef = { current: activeBotId };
      // See hasContinuationOccurredRef in useStreamSend — same duplicate-parts guard.
      const hasContinuationOccurredRef = { current: false };

      try {
        while (true) {
          let done: boolean;
          let value: Uint8Array | undefined;
          try {
            ({ done, value } = await reader.read());
          } catch {
            // reader.cancel() was called (stall timer / ceiling timer / early exit).
            // The retry is already scheduled; just exit this attempt cleanly.
            break;
          }
          if (done) break;
          onActivity();
          buffer += decoder.decode(value, { stream: true });
          // SSE messages are delimited by double newlines
          const sseMessages = buffer.split('\n\n');
          buffer = sseMessages.pop() ?? '';
          for (const msg of sseMessages) {
            const lines = msg.split('\n');
            let eventId: string | null = null;
            let eventDataLine: string | null = null;
            for (const line of lines) {
              if (line.startsWith('id: '))        { eventId = line.slice(4).trim(); }
              else if (line.startsWith('data: ')) { eventDataLine = line; }
              else if (line.startsWith(': '))     { onActivity(); /* heartbeat comment */ }
            }
            if (!eventDataLine) continue;
            try {
              const event = JSON.parse(eventDataLine.slice(6));

              // Track last event ID for SSE resume on retry
              if (eventId) {
                lastEventId = eventId;
              }

              const currentId = activeBotIdRef.current;

              // ── Idempotency replay — server re-emitted a cached final ──
              if (event.data?.replayed === true) {
                // Finalise the active bubble without any further processing
                setMessages(prev =>
                  prev.map(m => (m.id !== activeBotIdRef.current ? m : finalizeMessage(m))),
                );
                reader.cancel().catch(() => {}); // close the response body
                cleanup();
                _outerResolve();
                return; // exit attemptStream — replay is complete
              }

              // ── Intermediate final with server-side continuation ───────
              if (event.data?.is_final && event.data?.has_continuation) {
                const newBotId = `a_cont_${Date.now()}`;
                const newPlaceholder: Message = {
                  id: newBotId,
                  sender: 'bot',
                  text: '',
                  vin: null,
                  datasetId: null,
                  timestamp: new Date(),
                  isStreaming: true,
                  events: [],
                };
                const contFrame = parseStreamEvent(event) as ContinuationFrame;
                setMessages(prev => {
                  const updated = prev.map(m =>
                    m.id !== currentId ? m : reduceFrame(m, contFrame),
                  );
                  return [...updated, newPlaceholder];
                });
                hasContinuationOccurredRef.current = true;
                activeBotIdRef.current = newBotId;
                activeBotId = newBotId; // keep outer ref in sync for retry/reset
                continue;
              }

              // ── Terminal final event — server is done, close the bubble ──
              // The backend yields {"type":"final","data":{"content":...,"status":"success"}}
              // as the very last SSE frame before closing the stream.  We must handle it
              // explicitly here so the bubble is finalised immediately rather than waiting
              // for the reader to return done=true (which can be delayed by TCP buffering
              // or proxy keep-alive frames and leaves the UI stuck in a loading state).
              if (event.type === 'final') {
                // ── Reflect-and-retry plugin error leak guard (final event) ──
                // The plugin blob can arrive as a "final" event when all ADK retries
                // are exhausted.  Never show it to the user — treat as server_error.
                if (event.data?.content) {
                  const _trimmedFinal = (event.data.content as string).trim();
                  if (_trimmedFinal.startsWith('{')) {
                    try {
                      const _parsedFinal = JSON.parse(_trimmedFinal);
                      if (_parsedFinal?.response_type === 'ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN') {
                        reader.cancel().catch(() => {});
                        cleanup();
                        _outerReject(new Error('server_error'));
                        return;
                      }
                    } catch { /* not JSON — fall through */ }
                  }
                }
                // Terminal final (no continuation). Merge as a final event so
                // `finalizeMessage` can pick it up.
                const frame = parseStreamEvent(event) as FinalFrame;
                const contFrame = hasContinuationOccurredRef.current
                  ? ({ ...frame, kind: 'continuation', payload: { ...frame.payload, parts_result: [] } } as ContinuationFrame)
                  : ({ ...frame, kind: 'continuation' } as ContinuationFrame);
                setMessages(prev =>
                  prev.map(m => (m.id !== activeBotIdRef.current ? m : reduceFrame(m, contFrame))),
                );
                reader.cancel().catch(() => {}); // close the response body
                cleanup();
                _outerResolve();
                return; // exit attemptStream — stream is finished
              }

              // ── Backend error event ─────────────────────────────────────
              if (event.type === 'error') {
                // Always map the raw backend content to a safe, opaque sentinel
                // so internal error details (stack traces, exception classes, ADK
                // plugin blobs, etc.) are never propagated to the guest UI.
                // Allowed pass-through values are the handful of known sentinel
                // strings that drive specific UX flows (session_not_found, auth,
                // rate_limit).  Everything else becomes 'server_error'.
                const rawContent: string = event.data?.content ?? '';
                const ALLOWED_SENTINELS = new Set([
                  'session_not_found',
                  'auth',
                  'rate_limit',
                  'server_error',
                ]);
                // Treat "not found … session" patterns as a retriable session miss
                const isSessionMiss =
                  rawContent.toLowerCase().includes('not found') &&
                  rawContent.toLowerCase().includes('session');
                const errContent: string = isSessionMiss
                  ? 'session_not_found'
                  : ALLOWED_SENTINELS.has(rawContent)
                    ? rawContent
                    : 'server_error';

                if (errContent === 'session_not_found') {
                  // The backend self-healing path recreates the ADK session under
                  // the same session_id before emitting this event, so a plain
                  // retry of the same request (same session_id + message) will
                  // succeed.  Trigger the built-in retry mechanism rather than
                  // tearing down the session.
                  if (retryCount < MAX_RETRY_ATTEMPTS) {
                    errorFired = true;
                    readerRef?.cancel();
                    cleanup();
                    readerRef = null;
                    retryCount++;
                    resetActiveBubble();
                    const delay = Math.min(RETRY_BASE_DELAY * Math.pow(2, retryCount - 1), RETRY_MAX_DELAY);
                    setTimeout(() => {
                      errorFired = false;
                      attemptStream()
                        .then(() => { outerSettled = true; _outerResolve(); })
                        .catch((err) => { if (!outerSettled) _outerReject(err); });
                    }, delay);
                    return; // exit this attempt — retry is scheduled
                  }
                  // All retries exhausted — propagate so caller can handle gracefully
                  throw new Error('session_not_found');
                }
                throw new Error(errContent);
              }

              // ── Reflect-and-retry plugin error leak guard ────────────────
              // The ADK ReflectAndRetryToolPlugin emits a JSON blob with
              // response_type=ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN when a
              // tool call (e.g. validation_agent) fails with a 504/timeout.
              // The backend suppresses and retries these transparently, but if
              // all retries are exhausted the blob can still surface as event
              // content.  Never show it to the user — treat it as a retriable
              // server error so the retry button appears instead.
              if (event.data?.content) {
                let _maybeErrorJson: any = null;
                const _trimmed = (event.data.content as string).trim();
                if (_trimmed.startsWith('{')) {
                  try { _maybeErrorJson = JSON.parse(_trimmed); } catch { /* not JSON */ }
                }
                if (
                  _maybeErrorJson?.response_type === 'ERROR_HANDLED_BY_REFLECT_AND_RETRY_PLUGIN'
                ) {
                  // Discard this event entirely — do not update the bubble.
                  // If it is the final event, throw to trigger the retry/error UI.
                  if (event.data?.is_final) {
                    throw new Error('server_error');
                  }
                  continue;
                }
              }

              // Normal event — update the active bot bubble
              {
                const frame = parseStreamEvent(event);
                if (frame.kind === 'intermediate' || frame.kind === 'continuation') {
                  setMessages(prev =>
                    prev.map(m => (m.id !== activeBotIdRef.current ? m : reduceFrame(m, frame))),
                  );
                } else if (frame.kind === 'final') {
                  // is_final=true, has_continuation=false: fold the payload into
                  // events and finalize the bubble so parts_result / not_found_parts
                  // etc. are captured. Mirrors the continuation path without
                  // spawning a new placeholder bubble.
                  const asCont = hasContinuationOccurredRef.current
                    ? ({ ...frame, kind: 'continuation', payload: { ...frame.payload, parts_result: [] } } as ContinuationFrame)
                    : ({ ...frame, kind: 'continuation' } as ContinuationFrame);
                  setMessages(prev =>
                    prev.map(m => (m.id !== activeBotIdRef.current ? m : reduceFrame(m, asCont))),
                  );
                }
              }

              // ── Detect UPDATE_USER_DETAILS action in event content ──────
              if (event.data?.content) {
                const udMatch = (event.data.content as string).match(
                  /UPDATE_USER_DETAILS:(\{[^}]*\})/,
                );
                if (udMatch) {
                  try {
                    const payload = JSON.parse(udMatch[1]);
                    handleUpdateUserDetails(payload);
                  } catch { /* ignore malformed payload */ }
                }
              }
            } catch (parseErr: any) {
              if (!(parseErr instanceof SyntaxError)) throw parseErr;
            }
          }
        }
      } finally {
        cleanup();
        // reader.cancel() may have already been called (from the stall timer or
        // the early-exit 'final' handler).  releaseLock() on an already-cancelled
        // reader can throw; swallow safely.
        try { reader.releaseLock(); } catch { /* already released */ }
      }

      // Stream ended normally — finalise whichever bubble is active
      setMessages(prev =>
        prev.map(m => (m.id !== activeBotIdRef.current ? m : finalizeMessage(m))),
      );
      _outerResolve();
    }; // end attemptStream

    // Kick off the first attempt; subsequent retries are scheduled from within
    // the stall-timer callback and also resolve/reject outerPromise.
    // Note: _outerResolve is idempotent (Promise resolves only once), so calling
    // it from both the early-exit path inside attemptStream AND from .then() here
    // is safe — the second call is a no-op.
    attemptStream()
      .then(() => { outerSettled = true; _outerResolve(); })
      .catch((err) => {
        // If errorFired is already true, the stall-timer or fetch-timeout already
        // arranged a retry or rejection — swallow this cancellation error.
        if (!outerSettled) _outerReject(err);
      });
    await outerPromise;
  }, [authHeaders, guestSession, handleUpdateUserDetails]);

  // ── Translate a thrown error into a user-facing string ──────────────────
  const friendlyErrorMessage = useCallback((e: any): string => {
    const msg: string = e?.message ?? String(e);
    if (msg === 'offline' || msg.includes('offline') || msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      return t('guest.chat.errorOffline', 'You appear to be offline. Please check your connection and try again.');
    }
    if (msg === 'auth') return t('guest.chat.errorAuth', 'Your session has expired. Please refresh the page.');
    if (msg === 'rate_limit') return t('guest.chat.errorRateLimit', 'Too many requests — please wait a moment and try again.');
    if (msg === 'server_error') return t('guest.chat.errorServer', 'The server encountered an error. Please try again.');
    if (msg === 'session_not_found') return t('guest.chat.errorSessionNotFound', 'Your session has expired. Starting a new session…');
    return t('guest.chat.error', 'Sorry, something went wrong. Please try again.');
  }, [t]);

  // ── Report error helpers ─────────────────────
  const openReportError = useCallback((errorMsg: string, context = 'chat') => {
    setReportErrorMessage(errorMsg);
    setReportErrorContext(context);
    setReportErrorComment('');
    setReportErrorDone(false);
    setReportErrorOpen(true);
  }, []);

  const submitErrorReport = useCallback(async () => {
    if (!guestSession) return;
    setReportErrorSubmitting(true);
    try {
      await fetch(`${BASE_URL}/api/guest/report-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${guestSession.access_token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          error_message: reportErrorMessage,
          user_comment: reportErrorComment || null,
          error_context: reportErrorContext,
        }),
      });
      setReportErrorDone(true);
    } catch {
      // non-fatal — we still show success to avoid confusing the user
      setReportErrorDone(true);
    } finally {
      setReportErrorSubmitting(false);
    }
  }, [guestSession, sessionId, reportErrorMessage, reportErrorComment, reportErrorContext]);

  // ── Step 3: send message ──────────────────────
  const sendMessage = async () => {
    if (!input.trim() && selectedFiles.length === 0) return;
    if (!sessionId || !guestSession || isLoadingRef.current) return;

    if (isRecording) stopAudio();

    const filesToSend = selectedFiles.map(f => ({ data: f.data, mimeType: f.mimeType }));
    const messageText = input.trim();

    const userMsgId = `u_${Date.now()}`;
    const userMsg: Message = {
      id: userMsgId,
      sender: 'user',
      text: messageText,
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: false,
      uploadedFiles: selectedFiles.length > 0 ? selectedFiles : undefined,
    };
    const assistantMsgId = `a_${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantMsgId,
      sender: 'bot',
      text: '',
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: true,
      events: [],
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput('');
    setSelectedFiles([]);
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage(messageText, assistantMsgId, controller.signal, filesToSend.length > 0 ? filesToSend : undefined);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        // User cancelled — remove the bot placeholder and restore the input
        setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
        setInput(messageText);
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      // Remove the empty bot placeholder and mark the user message with an error
      // so the standard retry banner / error UI renders correctly.
      setMessages(prev =>
        prev
          .filter(m => m.id !== assistantMsgId)
          .map(m =>
            m.id === userMsgId
              ? { ...m, hasError: true, errorDetails: { message: errorMsg } }
              : m,
          ),
      );
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  // ── Structured clarification submit ──
  // Sends the user's answer to an in-bubble clarification form. Mirrors
  // sendMessage but carries the structured_answer payload and a fixed
  // human-readable summary instead of free-text input.
  const submitClarification = async (answerMessage: string, structuredAnswer: StructuredAnswer) => {
    if (!sessionId || !guestSession || isLoadingRef.current) return;

    const userMsgId = `u_${Date.now()}`;
    const userMsg: Message = {
      id: userMsgId,
      sender: 'user',
      text: answerMessage,
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: false,
    };
    const assistantMsgId = `a_${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantMsgId,
      sender: 'bot',
      text: '',
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: true,
      events: [],
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsLoading(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage(answerMessage, assistantMsgId, controller.signal, undefined, undefined, undefined, structuredAnswer as unknown as Record<string, unknown>);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev
          .filter(m => m.id !== assistantMsgId)
          .map(m =>
            m.id === userMsgId
              ? { ...m, hasError: true, errorDetails: { message: errorMsg } }
              : m,
          ),
      );
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  };

  // ── Auto-trigger initial NEXT on new session creation ──
  // This sends the first 'NEXT' message to kick off the guest conversation.
  // Continuation actions within the conversation are now handled server-side.
  const handleAutoTriggerNext = useCallback(async () => {
    // Use refs for session data so we always have the freshest values even when
    // React state hasn't flushed yet (e.g. first incognito load, handleReset).
    const sid     = sessionIdRef.current;
    const session = guestSessionRef.current;
    if (isLoadingRef.current || !sid || !session) return;

    const botMsgId = `a_next_${Date.now()}`;
    const botMsg: Message = {
      id: botMsgId,
      sender: 'bot',
      text: '',
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: true,
      events: [],
    };

    setMessages(prev => [...prev, botMsg]);
    setIsLoading(true);
    isLoadingRef.current = true;

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Build the init message — append any available vehicle/car context so the
      // agent is aware of them from the very first turn.
      const carDetails: string[] = [];
      if (session.vin)           carDetails.push(`VIN: ${session.vin}`);
      if (session.license_plate) carDetails.push(`License Plate: ${session.license_plate}`);
      if (session.manufacturer)  carDetails.push(`Manufacturer: ${session.manufacturer}`);
      if (session.model)         carDetails.push(`Model: ${session.model}`);
      if (session.year)          carDetails.push(`Year: ${session.year}`);
      const initMessage = carDetails.length > 0
        ? `SYSTEM_ACTION:START_CHAT\n${carDetails.join('\n')}`
        : 'SYSTEM_ACTION:START_CHAT';

      // Pass overrides so streamToMessage bypasses stale React state entirely.
      await streamToMessage(initMessage, botMsgId, controller.signal, undefined, sid, session);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== botMsgId));
        return;
      }
      // Replace the streaming placeholder with a visible error message
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev.map(m =>
          m.id === botMsgId
            ? { ...m, isStreaming: false, isError: true, text: errorMsg }
            : m,
        ),
      );
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      abortRef.current = null;
    }
  }, [streamToMessage, friendlyErrorMessage]);

  // Auto-send NEXT when a brand-new session is created to kick off the conversation.
  // Sequentially polls GET /api/guest/chat/session/{sid} with no delays — each fetch
  // completes before the next one starts.  The moment the backend returns 200 we fire
  // handleAutoTriggerNext.  No timers, no arbitrary waits — purely linear I/O.
  useEffect(() => {
    if (!sessionId || !shouldAutoTriggerRef.current) return;
    shouldAutoTriggerRef.current = false;

    let cancelled = false;
    const MAX_POLL_ATTEMPTS = 20;

    const pollThenTrigger = async () => {
      const sid     = sessionIdRef.current;
      const session = guestSessionRef.current;
      if (!sid || !session) return;

      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          const res = await fetch(`${BASE_URL}/api/guest/chat/session/${sid}`, {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (res.ok) {
            if (!cancelled) handleAutoTriggerNext();
            return;
          }
        } catch {
          // Network hiccup — try again immediately
        }
      }
      // All attempts exhausted — fire anyway (backend has its own retry loop)
      if (!cancelled) handleAutoTriggerNext();
    };

    pollThenTrigger();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ── Mobile background/foreground recovery ─────────────────────────────────
  // When the user switches to another app on mobile and comes back, the browser
  // may silently kill the SSE fetch stream.  We listen for the page becoming
  // visible again and:
  //   1. If a stream was in progress AND it has been stalled for too long
  //      (> STALL_ABORT_MS with no data) → abort the dead controller so the UI
  //      is not stuck in a loading spinner.  We do NOT abort streams that are
  //      still actively receiving data — the stall-timer inside streamToMessage
  //      already handles dead streams with automatic retries.
  //   2. If no stream is in progress → reload session history so any messages
  //      that arrived server-side while the tab was hidden are shown immediately.
  const lastStreamDataRef = useRef<number>(0);
  useEffect(() => {
    const STALL_ABORT_MS = 30_000; // abort only if hidden >30 s with no data

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const sid     = sessionIdRef.current;
      const session = guestSessionRef.current;
      if (!sid || !session) return;

      if (isLoadingRef.current && abortRef.current) {
        const msSinceData = Date.now() - lastStreamDataRef.current;
        if (msSinceData > STALL_ABORT_MS) {
          // Stream appears truly dead — release the UI lock.
          console.log(`[guest visibility] Tab resumed after ${msSinceData}ms stall — aborting dead stream`);
          abortRef.current.abort();
          abortRef.current = null;
          isLoadingRef.current = false;
          setIsLoading(false);
          // Reload history to show any server-side progress
          loadSessionHistory(sid, session.access_token);
        } else {
          // Stream is still alive (stall-timer will handle it if needed) — don't abort.
          console.log(`[guest visibility] Tab resumed — stream active (last data ${msSinceData}ms ago), not aborting`);
        }
        return;
      }

      // No active stream — reload history to show background updates.
      console.log('[guest visibility] Tab resumed — refreshing session history');
      loadSessionHistory(sid, session.access_token);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty — uses refs so no stale-closure risk

  const handleKeyDown = (e: React.KeyboardEvent) => {    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
    const maxSize = 10 * 1024 * 1024;
    Array.from(files).forEach(file => {
      if (!validTypes.includes(file.type)) {
        showToast(`File type not supported: ${file.name}`);
        return;
      }
      if (file.size > maxSize) {
        showToast(`File too large (max 10 MB): ${file.name}`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setSelectedFiles(prev => [...prev, {
          data: result.split(',')[1],
          mimeType: file.type,
          preview: result,
          name: file.name,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }, [showToast]);

  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // ── Retry: re-send a failed user message ───────────────────────────────
  const handleRetry = useCallback(async (msg: Message) => {
    if (!sessionId || !guestSession || isLoadingRef.current) return;
    const messageText = msg.text ?? '';
    const retryFiles = (msg as any).uploadedFiles
      ? (msg as any).uploadedFiles.map((f: any) => ({ data: f.data, mimeType: f.mimeType }))
      : undefined;

    // Clear the error flag on the user message and add a new bot placeholder
    const newBotMsgId = `a_retry_${Date.now()}`;
    setMessages(prev => {
      const cleared = prev.map(m =>
        m.id === msg.id ? { ...m, hasError: false, errorDetails: undefined } : m,
      );
      return [
        ...cleared,
        {
          id: newBotMsgId,
          sender: 'bot' as const,
          text: '',
          vin: null,
          datasetId: null,
          timestamp: new Date(),
          isStreaming: true,
          events: [],
        },
      ];
    });

    setIsLoading(true);
    isLoadingRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamToMessage(messageText, newBotMsgId, controller.signal, retryFiles);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== newBotMsgId));
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev
          .filter(m => m.id !== newBotMsgId)
          .map(m =>
            m.id === msg.id
              ? { ...m, hasError: true, errorDetails: { message: errorMsg } }
              : m,
          ),
      );
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
      abortRef.current = null;
    }
  }, [sessionId, guestSession, streamToMessage, friendlyErrorMessage]);

  // ── Retry handler for bot error bubbles ──────────────────────────────────
  // BotMessage calls onRetry(botMessage) when the user clicks Retry on an
  // isError bubble.  We find the preceding user message and re-send it.
  // For the initial auto-trigger bubble (no preceding user message) we
  // re-trigger handleAutoTriggerNext instead.
  const handleBotRetry = useCallback((botMsg: Message) => {
    setMessages(prev => {
      const idx = prev.findIndex(m => m.id === botMsg.id);
      // Look for the most-recent user message before this bot bubble
      const prevUserMsg = idx > 0
        ? [...prev.slice(0, idx)].reverse().find(m => m.sender === 'user')
        : undefined;

      if (prevUserMsg) {
        // Re-send the user message (removes the errored bot bubble, re-streams)
        // We schedule the async work outside of the state updater
        setTimeout(() => handleRetry(prevUserMsg), 0);
        // Remove only the errored bot bubble for now; handleRetry adds a fresh one
        return prev.filter(m => m.id !== botMsg.id);
      }

      // No preceding user message → initial greeting bubble; re-trigger NEXT
      setTimeout(() => handleAutoTriggerNext(), 0);
      return prev.filter(m => m.id !== botMsg.id);
    });
  }, [handleRetry, handleAutoTriggerNext]);

  const noopFeedback = useCallback((_id: string, _helpful: boolean) => {}, []);
  const noopSearchPart = useCallback((text: string) => {
    if (text) setInput(text);
  }, []);

  // Programmatic send used by the suggested-action chips (BotMessage's
  // onSendSilent) — same flow as sendMessage but with the chip's text instead
  // of the input field.
  const sendSuggested = useCallback(async (text: string) => {
    const messageText = (text || '').trim();
    if (!messageText || !sessionId || !guestSession || isLoadingRef.current) return;
    const userMsg: Message = {
      id: `u_${Date.now()}`,
      sender: 'user',
      text: messageText,
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: false,
    };
    const assistantMsgId = `a_${Date.now()}`;
    const assistantMsg: Message = {
      id: assistantMsgId,
      sender: 'bot',
      text: '',
      vin: null,
      datasetId: null,
      timestamp: new Date(),
      isStreaming: true,
      events: [],
    };
    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamToMessage(messageText, assistantMsgId, controller.signal);
    } catch (e: any) {
      if (e.name === 'AbortError') {
        setMessages(prev => prev.filter(m => m.id !== assistantMsgId));
        return;
      }
      const errorMsg = friendlyErrorMessage(e);
      setMessages(prev =>
        prev
          .filter(m => m.id !== assistantMsgId)
          .concat({
            id: `e_${Date.now()}`,
            sender: 'bot',
            text: errorMsg,
            vin: null,
            datasetId: null,
            timestamp: new Date(),
            isStreaming: false,
            isError: true,
          }),
      );
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, guestSession, streamToMessage]);
  const noopErrorToggle = useCallback((_id: string) => {}, []);
  const noopErrorCopy = useCallback((_details: any, _id: string) => {}, []);

  // Open the report-error modal pre-filled with a message's error details
  const handleReportMessageError = useCallback((msg: Message) => {
    const errMsg = msg.errorDetails?.message ?? msg.text ?? 'Unknown error';
    openReportError(errMsg, 'chat_message');
  }, [openReportError]);

  const handleReset = useCallback(async () => {
    if (!guestSession) return;
    setCartItems([]);
    setCartSubmitted(false);
    setShowCart(false);
    setMessages([]);
    sessionIdRef.current = null; // clear immediately so stale ID can't leak into next session
    setSessionId(null);
    sessionCreatingRef.current = false; // allow startChatSession to run again
    // Strip sid from URL
    const next = new URLSearchParams(searchParams);
    next.delete('sid');
    window.history.replaceState(null, '', next.toString() ? `?${next.toString()}` : window.location.pathname);
    shouldAutoTriggerRef.current = true;
    await startChatSessionRef.current(guestSession);
  }, [guestSession, searchParams]);

  // ── Cart handlers ─────────────────────────────
  const handleAddToCart = useCallback((part: any, structuredData: any) => {
    setCartItems(prev => {
      const partId =
        part?.part_callout_id ||
        structuredData?.part_data?.part_id ||
        structuredData?.PART_DATA?.PART_ID ||
        part?.structured_data?.part_data?.part_id;
      // Prevent duplicates
      if (partId && prev.some(item => {
        const existId =
          item.part_callout_id ||
          item.structured_data?.part_data?.part_id ||
          item.structured_data?.PART_DATA?.PART_ID;
        return existId === partId;
      })) {
        return prev;
      }
      const newItem = {
        ...part,
        structured_data: structuredData || part?.structured_data,
        quantity: parseCatalogQuantity(part?.quantity),
        timestamp: new Date(),
      };
      return [...prev, newItem];
    });
    setShowCart(true);
    setCartSubmitted(false);
  }, []);

  const handleRemoveFromCart = useCallback((index: number) => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdateQuantity = useCallback((index: number, qty: number) => {
    if (qty < 1) return;
    setCartItems(prev => prev.map((item, i) => i === index ? { ...item, quantity: qty } : item));
  }, []);

  const handleSubmitCart = useCallback(async () => {
    if (!sessionId || !guestSession || cartItems.length === 0) return;
    setIsSubmittingCart(true);
    try {
      const res = await fetch(`${BASE_URL}/api/guest/cart/submit`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          items: cartItems,
          visitor_name: guestSession.visitor_name ?? undefined,
          visitor_email: guestSession.visitor_email ?? undefined,
          visitor_phone: guestSession.visitor_phone ?? undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? `Error ${res.status}`);
      }
      setCartSubmitted(true);
      // Propagate the submission event to the parent page (SDK host site) via postMessage.
      // The CataxSDK _bindMessageListener picks this up and fires it as a 'cart_submitted' event
      // that host-site developers can subscribe to via sdk.on('cart_submitted', fn) or onEvent.

      const results = await res.json();
      debugger;
      window.parent.postMessage(
        {
          cataxEvent: 'cart_submitted',
          session_id: sessionId,
          items: results.items,
          order: results.order
        },
        '*',
      );
      // Clear persisted cart — order has been submitted
      if (cartStorageKey) sessionStorage.removeItem(cartStorageKey);
      // After showing the confirmation, reset to a fresh session
      setTimeout(async () => {
        setCartItems([]);
        setCartSubmitted(false);
        setShowCart(false);
        setMessages([]);
        sessionIdRef.current = null; // clear immediately so stale ID can't leak into next session
        setSessionId(null);
        sessionCreatingRef.current = false; // allow startChatSession to run for the new session
        // Strip sid from URL so startChatSession creates a brand-new session
        const next = new URLSearchParams(searchParams);
        next.delete('sid');
        window.history.replaceState(null, '', next.toString() ? `?${next.toString()}` : window.location.pathname);
        // Start a fresh chat session
        if (guestSession) await startChatSessionRef.current(guestSession);
      }, 3000);
    } catch (e: any) {
      showToast(t('guest.cart.submitError', 'Failed to submit cart. Please try again.'));
    } finally {
      setIsSubmittingCart(false);
    }
  }, [sessionId, guestSession, cartItems, cartStorageKey, authHeaders, t]);

  // ─────────────────────────────────────────────
  // Render: Loading / Error / Chat
  // ─────────────────────────────────────────────
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center space-y-5">
          <div className="relative mx-auto w-16 h-16">
            <div className="absolute inset-0 rounded-2xl bg-blue-500 opacity-20 animate-ping" />
            <div className="relative w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg">
              <Bot className="w-8 h-8 text-white" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-2.5 w-32 bg-gray-200 rounded-full mx-auto animate-pulse" />
            <div className="h-2 w-20 bg-gray-100 rounded-full mx-auto animate-pulse" />
          </div>
          <p className="text-gray-400 text-xs">{t('guest.loading', 'Starting your session…')}</p>
        </div>
      </div>
    );
  }

  if (domainBlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-amber-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-amber-100">
          <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-amber-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('guest.error.domainBlocked', 'Access Restricted')}
          </h2>
          <p className="text-sm text-gray-500">
            {t('guest.error.domainBlockedMessage', 'This link is not authorised to be used from this website.')}
          </p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-red-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('guest.error.title', 'Something went wrong')}
          </h2>
          <p className="text-sm text-gray-500 mb-6">{initError}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-gray-700 transition-colors"
            >
              {t('guest.error.back', 'Try again')}
            </button>
            {guestSession && (
              <button
                onClick={() => openReportError(initError, 'init')}
                className="px-5 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2 justify-center"
              >
                <Flag className="w-4 h-4" />
                {t('guest.error.reportError', 'Report this error')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const expandedErrors = new Set<string>();

  const totalCartQuantity = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  return (
    <OrderProvider>
      {/* ── Toast stack ──────────────────────────────────────────────────── */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[300] flex flex-col gap-2 pointer-events-none w-full max-w-sm px-4">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium pointer-events-auto animate-fade-in
              ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'}`}
          >
            {toast.type === 'error'
              ? <AlertCircle className="w-4 h-4 flex-shrink-0" />
              : <Sparkles className="w-4 h-4 flex-shrink-0" />}
            <span className="flex-1">{toast.message}</span>
            <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))} className="opacity-70 hover:opacity-100 transition-opacity">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="flex flex-col overflow-hidden" style={{ height: '100dvh', background: '#f8f8f6' }}>
        {/* ── Offline banner ───────────────────────────────────────────────── */}
        {!isOnline && (
          <div className="flex-shrink-0 flex items-center justify-center gap-2 bg-amber-500 text-white text-xs font-semibold py-2 px-4">
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            {t('guest.chat.offline', 'You appear to be offline — messages will send when reconnected.')}
          </div>
        )}

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <header className="flex-shrink-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3"
          style={{ boxShadow: '0 1px 0 0 #f0ede8' }}>
          {/* Avatar / logo */}
          <div className={`w-9 h-9 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center shadow-sm ${guestSession?.icon_url ? 'bg-white border border-gray-100' : 'bg-blue-600'}`}>
            {guestSession?.icon_url ? (
              <img src={guestSession.icon_url} alt="" className="w-full h-full object-contain p-0.5" />
            ) : (
              <Bot className="w-5 h-5 text-white" />
            )}
          </div>

          {/* Title + status */}
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-gray-900 truncate leading-tight">
              {guestSession?.header_text || guestSession?.organization_name || ''}
            </h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-emerald-400' : 'bg-amber-400'}`} />
              <span className="text-[11px] text-gray-400 leading-none">
                {isLoading
                  ? t('guest.header.thinking', 'Thinking…')
                  : isOnline
                    ? t('guest.header.online', 'Online')
                    : t('guest.header.offline', 'Offline')}
              </span>
            </div>
          </div>

          {/* Cart button */}
          {cartItems.length > 0 && (
            <button
              onClick={() => setShowCart(true)}
              className="relative flex items-center gap-1.5 px-3 py-1.5 bg-[#201e1a] text-white text-xs font-semibold rounded-lg hover:bg-[#3a3835] active:scale-95 transition-all"
            >
              <ShoppingCart className="w-4 h-4" />
              <span className="hidden sm:inline">{t('guest.cart.title', 'Cart')}</span>
              <span className="bg-indigo-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold px-1">
                {totalCartQuantity}
              </span>
            </button>
          )}

          {/* New session */}
          <button
            onClick={handleReset}
            disabled={isLoading}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 transition-all"
            title={t('guest.header.newSession', 'Start new session')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </header>

        {/* ── Messages ─────────────────────────────────────────────────────── */}
        <main
          ref={mainScrollRef}
          className="flex-1 overflow-y-auto px-3 sm:px-6 pt-6 pb-4 space-y-6 max-w-3xl mx-auto w-full"
        >
          <CatalogStatusStrip messages={messages} />
          {/* Requested-parts tracker — replaces the legacy in-bubble list. */}
          <PartsTrackerPanel
            items={trackedParts}
            serviceTitle={trackedServiceTitle}
            foundParts={trackerFoundParts}
            notFoundParts={trackerNotFoundParts}
            annotatedImages={trackerAnnotatedImages}
            visionPartImages={trackerVisionPartImages}
            cartItems={cartItems}
            isSearching={isLoading}
            onSendSilent={sendSuggested}
          />

          {/* Empty state */}
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center pt-12 pb-6 gap-5">
              <div className="w-20 h-20 rounded-2xl bg-white shadow-md flex items-center justify-center border border-gray-100">
                {guestSession?.icon_url ? (
                  <img src={guestSession.icon_url} alt="" className="w-12 h-12 object-contain" />
                ) : (
                  <Sparkles className="w-9 h-9 text-blue-500" />
                )}
              </div>
              <div className="space-y-1">
                <p className="text-gray-800 font-semibold text-base">
                  {guestSession?.header_text || t('guest.chat.emptyTitle', 'How can I help you today?')}
                </p>
                <p className="text-gray-400 text-sm max-w-xs">
                  {t('guest.chat.emptySubtitle', 'Ask me anything about your vehicle or auto parts.')}
                </p>
              </div>
              {/* Quick-start chips */}
              <div className="flex flex-wrap gap-2 justify-center max-w-xs">
                {[
                  t('guest.chat.chip1', 'Find a part'),
                  t('guest.chat.chip2', 'Check compatibility'),
                  t('guest.chat.chip3', 'Get a repair quote'),
                ].map((chip) => (
                  <button
                    key={chip}
                    onClick={() => { setInput(chip); }}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-white border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 active:scale-95 transition-all shadow-sm"
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.filter(msg => !msg.text?.startsWith('SYSTEM_EVENT:')).map((msg, idx, arr) => (
            msg.sender === 'user' && isPersistedPartEditInstruction(msg.text) ? (
              <div key={msg.id} className="my-4 flex justify-center" role="status">
                <div className="flex max-w-lg items-start gap-2 rounded-xl border border-violet-200/80 bg-violet-50/90 px-3 py-2 text-xs text-violet-800 shadow-sm">
                  <PencilLine className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="font-semibold">
                      {t('chat.partEdit.updatedIndicator', 'Part search updated')}
                    </div>
                    {parsePersistedPartEdits(msg.text).map((edit, editIndex) => (
                      <div key={`${edit.original}-${edit.value}-${editIndex}`} className="mt-0.5 break-words text-violet-700/90">
                        <span className="line-through opacity-70">{edit.original}</span>
                        <span className="mx-1.5" aria-hidden="true">→</span>
                        <span className="font-medium">{edit.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : msg.sender === 'user' ? (
              <div key={msg.id}>
                <UserMessage
                  message={msg}
                  isLoading={isLoading}
                  expandedErrors={expandedErrors}
                  errorTooltipRef={React.createRef()}
                  onRetry={handleRetry}
                  onToggleErrorDetails={noopErrorToggle}
                  onCopyErrorDetails={noopErrorCopy}
                  formatTime={formatTime}
                />
                {/* Retry + report error — below failed user message */}
                {(msg as any).hasError && guestSession && (
                  <div className="flex justify-end items-center gap-3 mt-2 pr-12">
                    <button
                      onClick={() => handleRetry(msg)}
                      disabled={isLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
                    >
                      <RotateCcw className="w-3 h-3" />
                      {t('guest.chat.retry', 'Retry')}
                    </button>
                    <button
                      onClick={() => handleReportMessageError(msg)}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Flag className="w-3 h-3" />
                      {t('guest.error.reportError', 'Report this error')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <BotMessage
                key={msg.id}
                message={msg}
                persistedAnnotatedImages={annotatedImageAssignments.get(msg.id)}
                isLastMessage={idx === arr.length - 1}
                isLoading={isLoading}
                addingToCartItems={new Set()}
                onRetry={handleBotRetry}
                onFeedback={noopFeedback}
                onSearchPart={noopSearchPart}
                onSendSilent={sendSuggested}
                onSubmitClarification={submitClarification}
                onAddToCart={handleAddToCart}
                onValidationParts={handleValidationParts}
                validationItems={trackedParts}
                onOrderUpdate={() => {}}
                formatTime={formatTime}
                isValidImageData={(d) => !!d && d.length > 50}
                cartItems={cartItems}
                onOpenCartPanel={() => setShowCart(true)}
                conversationalFollowups={false}
              />
            )
          ))}
          <div ref={bottomRef} />
        </main>

        {/* ── Scroll-to-bottom FAB ─────────────────────────────────────────── */}
        {showScrollBtn && (
          <button
            onClick={() => scrollToBottom('smooth')}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center text-gray-500 hover:bg-gray-50 active:scale-95 transition-all"
            aria-label="Scroll to bottom"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        )}

        {/* ── Input footer ─────────────────────────────────────────────────── */}
        <footer
          className="flex-shrink-0 bg-white border-t border-gray-100 px-4 pt-3 pb-3"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))', boxShadow: '0 -1px 0 0 #f0ede8' }}
        >
          <div className="max-w-3xl mx-auto">
            {/* File previews */}
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="relative group">
                    {file.mimeType.startsWith('image/') ? (
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="h-16 rounded-xl border border-gray-200 object-cover shadow-sm"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-xl border border-gray-200 bg-blue-50 flex flex-col items-center justify-center shadow-sm">
                        <FileText className="w-6 h-6 text-blue-500 mb-0.5" />
                        <span className="text-[10px] text-blue-600 font-medium truncate w-12 text-center px-1">
                          {file.name}
                        </span>
                      </div>
                    )}
                    <button
                      onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm transition hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Audio error inline */}
            {audioError && (
              <p className="text-xs text-amber-600 font-medium mb-1.5 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {audioError}
              </p>
            )}

            {/* Input box */}
            <div className={`relative rounded-2xl bg-white transition-shadow
              ${isRecording
                ? 'ring-2 ring-red-400 shadow-sm'
                : 'ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:shadow-md shadow-sm'}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.txt,text/plain,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Waveform while recording */}
              {isRecording ? (
                <div className="px-4 py-3">
                  <RecordingWaveform onStop={stopAudio} />
                </div>
              ) : (
                <textarea
                  rows={1}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading && !abortRef.current}
                  placeholder={
                    isProcessingAudio
                      ? t('chat.audio.processing', 'Processing audio…')
                      : t('guest.chat.inputPlaceholder', 'Type your question…')
                  }
                  className="w-full resize-none bg-transparent px-4 pt-3 pb-12 text-sm focus:outline-none disabled:text-gray-400 max-h-36 overflow-y-auto text-gray-900 placeholder-gray-400"
                />
              )}

              {/* Toolbar row */}
              {!isRecording && (
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  {/* Left: attach + mic + report */}
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading}
                      className="w-8 h-8 rounded-lg text-gray-400 flex items-center justify-center hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
                      title={t('chat.uploadFile', 'Upload file')}
                    >
                      <Paperclip className="w-4 h-4" />
                    </button>

                    {audioSupported && (
                      <button
                        onClick={toggleAudio}
                        disabled={isLoading || isProcessingAudio}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed ${
                          isRecording
                            ? 'bg-red-100 text-red-500 animate-pulse'
                            : isProcessingAudio
                            ? 'bg-blue-50 text-blue-500'
                            : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                        title={isRecording
                          ? t('chat.audio.stopRecording', 'Stop recording')
                          : t('chat.audio.startRecording', 'Record audio message')
                        }
                      >
                        {isRecording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </button>
                    )}

                    {guestSession && (
                      <button
                        onClick={() => openReportError('', 'chat')}
                        className="w-8 h-8 rounded-lg text-gray-400 flex items-center justify-center hover:bg-gray-100 hover:text-red-500 active:scale-95 transition-all"
                        title={t('guest.error.reportError', 'Report an issue')}
                      >
                        <Flag className="w-4 h-4" />
                      </button>
                    )}

                    {/* Char hint — only show when input is non-empty */}
                    {input.length > 0 && (
                      <span className={`text-[10px] ml-1 tabular-nums ${input.length > 1800 ? 'text-red-400 font-semibold' : 'text-gray-300'}`}>
                        {input.length}
                      </span>
                    )}
                  </div>

                  {/* Right: Stop (when loading) or Send */}
                  {isLoading && abortRef.current ? (
                    <button
                      onClick={() => abortRef.current?.abort()}
                      className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 active:scale-95 transition-all border border-red-200"
                      title={t('guest.chat.stop', 'Stop generating')}
                    >
                      <Square className="w-3 h-3 fill-current" />
                      <span className="hidden sm:inline">{t('guest.chat.stop', 'Stop')}</span>
                    </button>
                  ) : (
                    <button
                      onClick={sendMessage}
                      disabled={isLoading || (!input.trim() && selectedFiles.length === 0)}
                      className="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700 disabled:opacity-25 disabled:cursor-not-allowed active:scale-95 transition-all"
                    >
                      <Send className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Powered by */}
          <p className="text-center text-[11px] text-gray-300 mt-2">
            <a
              href="https://catax.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-500 transition-colors"
            >
              {t('guest.chat.poweredBy', 'Powered by CATA AI')}
            </a>
          </p>
        </footer>
      </div>

      {/* Cart Drawer */}
      {showCart && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[110]"
            onClick={() => setShowCart(false)}
          />
          {/* Panel */}
          <div className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-xl z-[120] flex flex-col animate-slideInRight border-l border-[#efebe2]">
            {/* Cart Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-[#efebe2] bg-white flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <ShoppingCart className="w-5 h-5 text-[#201e1a]" />
                <h2 className="text-base font-semibold text-[#201e1a]">
                  {t('guest.cart.title', 'Cart')}{cartItems.length > 0 ? ` (${cartItems.length})` : ''}
                </h2>
              </div>
              <button onClick={() => setShowCart(false)} className="p-2 hover:bg-[#f6f4f0] rounded-lg transition-colors">
                <X className="w-5 h-5 text-[#201e1a]" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4">
              {cartItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-[#999999]">
                  <Package className="w-10 h-10 mb-2 text-[#b2b2b2]" />
                  <p className="text-sm">{t('guest.cart.empty', 'Your cart is empty')}</p>
                </div>
              ) : (
                <div className="bg-[#f6f4f0] rounded-[10px] p-4">
                  <div className="divide-y divide-[#b2b2b2]">
                    {cartItems.map((item, index) => {
                      const partData = item.structured_data?.part_data || item.structured_data?.PART_DATA || {};
                      const oeNumber = displayPartRef(partData.part_number || partData.PART_NUMBER || partData.part_id || partData.PART_ID || item.partNumber || item.partId || '');
                      const description = partData.description || partData.DESCRIPTION || item.description || '';
                      const translatedDesc = item.translated_description || '';
                      const displayName = translatedDesc || description;
                      const subDescription = translatedDesc && translatedDesc !== description ? description : null;
                      const rawPrice = typeof partData.price === 'object' && partData.price !== null
                        ? (partData.price.price ?? partData.price.amount ?? null)
                        : partData.price;
                      const priceStr = rawPrice != null && String(rawPrice) !== 'undefined' && String(rawPrice) !== 'N/A'
                        ? String(rawPrice)
                        : null;
                      const priceCurrency = typeof partData.price === 'object' && partData.price !== null
                        ? (partData.price.currency ?? '')
                        : '';
                      const price = priceStr ? (priceCurrency ? `${priceStr} ${priceCurrency}` : priceStr) : null;
                      const thumbnail = item.image || null;
                      return (
                        <div key={index} className="py-3">
                          {/* Main two-column layout */}
                          <div className="flex items-start gap-2">
                            {/* Thumbnail */}
                            {thumbnail ? (
                              <div className="flex-shrink-0 w-9 h-9 rounded-lg overflow-hidden bg-white border border-[#efebe2] mt-0.5">
                                <img
                                  src={thumbnail}
                                  alt={oeNumber || 'part'}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white border border-[#efebe2] flex items-center justify-center mt-0.5">
                                <Package className="w-4 h-4 text-[#b2b2b2]" />
                              </div>
                            )}

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                              {oeNumber && (
                                <div className="font-mono text-xs font-semibold text-[#201e1a] mb-0.5">{oeNumber}</div>
                              )}
                              {displayName && (
                                <div className="text-xs text-[#555] line-clamp-2 leading-snug">{displayName}</div>
                              )}
                              {subDescription && (
                                <div className="text-[10px] text-[#999] line-clamp-1 mt-0.5">{subDescription}</div>
                              )}
                              {price && (
                                <div className="text-xs font-semibold text-[#201e1a] mt-1">{price}</div>
                              )}
                            </div>

                            {/* Remove */}
                            <button
                              onClick={() => handleRemoveFromCart(index)}
                              className="flex-shrink-0 p-0.5 text-[#999999] hover:text-red-500 transition-colors mt-0.5"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Quantity row */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#e5e3df]">
                            <div className="flex items-center gap-1 bg-white rounded-full px-1.5 py-0.5 shadow-sm border border-[#efebe2]">
                              <button
                                onClick={() => handleUpdateQuantity(index, (item.quantity || 1) - 1)}
                                disabled={(item.quantity || 1) <= 1}
                                className="p-1 text-[#555] hover:text-[#201e1a] disabled:opacity-30 transition-colors"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-2 text-xs font-semibold text-[#201e1a] min-w-[1.5rem] text-center">
                                {item.quantity || 1}
                              </span>
                              <button
                                onClick={() => handleUpdateQuantity(index, (item.quantity || 1) + 1)}
                                className="p-1 text-[#555] hover:text-[#201e1a] transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="text-[10px] text-[#999999]">{t('guest.cart.qty', 'qty')}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Cart Footer */}
            <div className="flex-shrink-0 border-t border-[#efebe2] bg-white px-4 py-4 space-y-3">
              {cartSubmitted ? (
                <div className="flex flex-col items-center gap-2 py-3 text-center">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <p className="text-sm font-semibold text-[#201e1a]">
                    {t('guest.cart.submitted', 'Your request has been submitted!')}
                  </p>
                  <p className="text-xs text-[#999999]">
                    {t('guest.cart.submittedNote', 'We will get back to you shortly.')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-[#201e1a]">{t('guest.cart.items', 'Total Items')}</span>
                      <span className="text-xs font-semibold text-[#201e1a]">{totalCartQuantity}</span>
                    </div>
                  </div>
                  <div className="border-t border-[#efebe2]" />

                  {/* Visitor details card */}
                  {(guestSession?.visitor_name || guestSession?.visitor_email || guestSession?.visitor_phone) && (
                    <div className="bg-[#f6f4f0] rounded-xl px-3 py-2.5 space-y-1">
                      <p className="text-[10px] font-semibold text-[#999] uppercase tracking-wide">
                        {t('guest.cart.contactLabel', 'Contact')}
                      </p>
                      {guestSession.visitor_name && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-semibold text-[#201e1a] truncate">
                            {guestSession.visitor_name}
                          </span>
                        </div>
                      )}
                      {guestSession.visitor_email && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-[#555] truncate">
                            {guestSession.visitor_email}
                          </span>
                        </div>
                      )}
                      {guestSession.visitor_phone && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] text-[#555] truncate">
                            {guestSession.visitor_phone}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleSubmitCart}
                    disabled={cartItems.length === 0 || isSubmittingCart}
                    className="w-full py-2.5 bg-[#201e1a] text-white text-sm font-semibold rounded-[10px] hover:bg-[#3a3835] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmittingCart ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    {isSubmittingCart
                      ? t('guest.cart.submitting', 'Submitting…')
                      : t('guest.cart.submit', 'Submit Request')}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Report Error Modal ─────────────────────────────────────────── */}
      {reportErrorOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 z-[200]"
            onClick={() => { if (!reportErrorSubmitting) setReportErrorOpen(false); }}
          />
          {/* Panel */}
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto bg-white rounded-2xl shadow-xl z-[210] p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-500 flex-shrink-0" />
                <h3 className="text-base font-semibold text-gray-900">
                  {t('guest.error.reportErrorTitle', 'Report an Issue')}
                </h3>
              </div>
              {!reportErrorSubmitting && (
                <button onClick={() => setReportErrorOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 transition">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              )}
            </div>

            {reportErrorDone ? (
              <div className="text-center py-4">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
                <p className="text-sm text-gray-700 font-medium">
                  {t('guest.error.reportErrorSuccess', 'Thank you! Your report has been submitted.')}
                </p>
                <button
                  onClick={() => setReportErrorOpen(false)}
                  className="mt-4 px-5 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-700 transition"
                >
                  {t('guest.error.back', 'Close')}
                </button>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-500 mb-3">
                  {t('guest.error.reportErrorDescription', 'Help us improve by describing what happened.')}
                </p>
                {/* Auto-filled error summary (read-only) */}
                {reportErrorMessage && (
                  <div className="mb-3 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700 line-clamp-3 break-words">
                    {reportErrorMessage}
                  </div>
                )}
                <textarea
                  rows={3}
                  value={reportErrorComment}
                  onChange={e => setReportErrorComment(e.target.value)}
                  disabled={reportErrorSubmitting}
                  placeholder={t('guest.error.reportErrorPlaceholder', 'Optional: describe what you were doing when the error occurred…')}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50"
                />
                <div className="flex gap-3 mt-4 justify-end">
                  <button
                    onClick={() => setReportErrorOpen(false)}
                    disabled={reportErrorSubmitting}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button
                    onClick={submitErrorReport}
                    disabled={reportErrorSubmitting}
                    className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {reportErrorSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {reportErrorSubmitting
                      ? t('guest.error.reportErrorSubmitting', 'Sending…')
                      : t('guest.error.reportErrorSubmit', 'Send Report')}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </OrderProvider>
  );
};

export default GuestChatPage;
